import { h, clear } from './dom.js'
import { on } from '../lib/bus.js'
import {
  state,
  setVideoFile,
  setVideoMeta,
  setStamp,
  clearStamp,
  selectMove,
  setPlaybackTime,
} from '../lib/state.js'
import { formatTime } from '../lib/format.js'
import { splitGeometry } from '../lib/split.js'
import { figurineLabel } from './notation.js'
import { toast } from './toast.js'

const SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2]

let stageEl = null
let video = null

// Displayed video content box (object-fit: contain) relative to the stage —
// the overlay and the export pipeline both position against this rectangle.
export function getContentBox() {
  const rect = stageEl.getBoundingClientRect()
  const { width: vw, height: vh } = state.video
  if (!vw || !vh) return { left: 0, top: 0, width: rect.width, height: rect.height }
  const scale = Math.min(rect.width / vw, rect.height / vh)
  const width = vw * scale
  const height = vh * scale
  return { left: (rect.width - width) / 2, top: (rect.height - height) / 2, width, height }
}

export function getStage() {
  return stageEl
}

// The 9:16 output frame, letterboxed inside the stage — split mode composes
// board and footage inside this rectangle (mirrors the 1080×1920 export).
export function getFrameBox() {
  const rect = stageEl.getBoundingClientRect()
  const scale = Math.min(rect.width / 9, rect.height / 16)
  const width = 9 * scale
  const height = 16 * scale
  return { left: (rect.width - width) / 2, top: (rect.height - height) / 2, width, height }
}

export function mountPlayer(parent) {
  video = h('video', { playsinline: '', preload: 'metadata', hidden: true })
  const frameOutline = h('div.frame-outline', { hidden: true })
  const dropTitle = h('h3', {}, 'Load your OTB footage')
  const browseBtn = h('button.btn.primary', { type: 'button' }, 'Browse for a video')
  const drop = h(
    'div.stage-drop',
    {},
    dropTitle,
    h('p', {}, 'Drop a video file here. It plays straight from your disk — nothing is uploaded anywhere.'),
    browseBtn,
  )
  stageEl = h('div.stage', {}, drop, video, frameOutline)

  // In split mode the video is cropped into the bottom band of the 9:16
  // frame (object-fit: cover); in overlay mode the stylesheet's contain
  // layout applies untouched.
  function layoutVideo() {
    const o = state.overlay
    const splitActive = o.mode === 'split' && state.video.url
    frameOutline.hidden = !splitActive
    if (!splitActive) {
      video.style.cssText = ''
      return
    }
    const frame = getFrameBox()
    const g = splitGeometry({
      frameW: frame.width,
      frameH: frame.height,
      share: o.split.share,
      videoW: state.video.width,
      videoH: state.video.height,
      pos: o.split.pos,
    })
    Object.assign(video.style, {
      left: `${frame.left}px`,
      top: `${frame.top + g.videoBand.y}px`,
      width: `${g.videoBand.w}px`,
      height: `${g.videoBand.h}px`,
      right: 'auto',
      bottom: 'auto',
      objectFit: 'cover',
      objectPosition: `${o.split.pos * 100}% ${o.split.pos * 100}%`,
    })
    Object.assign(frameOutline.style, {
      left: `${frame.left}px`,
      top: `${frame.top}px`,
      width: `${frame.width}px`,
      height: `${frame.height}px`,
    })
  }
  new ResizeObserver(layoutVideo).observe(stageEl)

  const fileInput = h('input', {
    type: 'file',
    accept: 'video/*',
    hidden: true,
    onChange: (e) => {
      const file = e.target.files?.[0]
      if (file) loadFile(file)
      e.target.value = ''
    },
  })
  browseBtn.addEventListener('click', () => fileInput.click())

  // --- Transport -----------------------------------------------------------
  const seekInput = h('input.seek-input', {
    type: 'range',
    min: '0',
    max: '0',
    step: '0.01',
    value: '0',
    'aria-label': 'Seek',
  })
  const ticks = h('div.seek-ticks')
  const playBtn = h('button.btn', { type: 'button', title: 'Play/pause (Space)' }, '▶')
  const readout = h(
    'div.time-readout',
    {},
    h('span.current', {}, '0:00.00'),
    h('span', {}, ' / 0:00.00'),
  )
  const speedLabel = h('span.speed-label', {}, '1×')
  const tagBtn = h(
    'button.btn.primary',
    { type: 'button', title: 'Tag the selected move at the current time (Enter)' },
    '⏺ Set timestamp',
  )

  // The button names the move it will stamp, so a whole game can be tagged
  // while watching only the video — key for phones, where the scoresheet
  // sits below the fold.
  function updateTagBtn() {
    const move = state.pgn.moves[state.selection]
    tagBtn.textContent = move ? `⏺ Tag ${figurineLabel(move)}` : '⏺ Set timestamp'
  }

  const transport = h(
    'div.transport',
    {},
    h('div.seek-wrap', {}, seekInput, ticks),
    h(
      'div.transport-row',
      {},
      playBtn,
      h('button.btn', { type: 'button', title: 'Back one frame (,)', onClick: () => stepFrames(-1) }, '⏮'),
      h('button.btn', { type: 'button', title: 'Forward one frame (.)', onClick: () => stepFrames(1) }, '⏭'),
      readout,
      h('span.transport-spacer'),
      tagBtn,
      h('button.btn', { type: 'button', title: 'Slower ([)', onClick: () => cycleSpeed(-1) }, '−'),
      speedLabel,
      h('button.btn', { type: 'button', title: 'Faster (])', onClick: () => cycleSpeed(1) }, '+'),
    ),
    fileInput,
  )

  parent.append(stageEl, transport)

  // --- Loading -------------------------------------------------------------
  function loadFile(file) {
    if (!file.type.startsWith('video/') && !/\.(mp4|mov|webm|mkv|m4v)$/i.test(file.name)) {
      toast(`"${file.name}" doesn't look like a video file`, { kind: 'warn' })
      return
    }
    setVideoFile({ url: URL.createObjectURL(file), fileName: file.name, size: file.size })
    video.src = state.video.url
    video.playbackRate = currentSpeed
  }

  stageEl.addEventListener('dragover', (e) => {
    e.preventDefault()
    drop.classList.add('dragover')
  })
  stageEl.addEventListener('dragleave', () => drop.classList.remove('dragover'))
  stageEl.addEventListener('drop', (e) => {
    e.preventDefault()
    drop.classList.remove('dragover')
    const file = e.dataTransfer?.files?.[0]
    if (file) loadFile(file)
  })

  function applyMetadata() {
    setVideoMeta({ duration: video.duration, width: video.videoWidth, height: video.videoHeight })
    seekInput.max = String(video.duration)
    publish()
  }

  video.addEventListener('loadedmetadata', () => {
    if (Number.isFinite(video.duration)) {
      applyMetadata()
      return
    }
    // Some WebMs (MediaRecorder screen/camera captures) report Infinity until
    // the element is forced past the end once. Probe with a bounded value —
    // absurdly large seeks can wedge the demuxer on malformed files.
    const onDurationChange = () => {
      if (!Number.isFinite(video.duration)) return
      video.removeEventListener('durationchange', onDurationChange)
      video.currentTime = 0
      applyMetadata()
    }
    video.addEventListener('durationchange', onDurationChange)
    setTimeout(() => {
      if (!Number.isFinite(video.duration)) video.currentTime = 86400
    }, 0)
  })

  video.addEventListener('click', () => togglePlay())

  // --- Playback state ------------------------------------------------------
  let rafId = null
  let scrubbing = false

  function publish() {
    setPlaybackTime(video.currentTime, !video.paused && !video.ended)
    updateReadout()
  }

  function tick() {
    publish()
    rafId = requestAnimationFrame(tick)
  }

  video.addEventListener('play', () => {
    playBtn.textContent = '⏸'
    cancelAnimationFrame(rafId)
    rafId = requestAnimationFrame(tick)
  })
  video.addEventListener('pause', () => {
    playBtn.textContent = '▶'
    cancelAnimationFrame(rafId)
    publish()
  })
  video.addEventListener('ended', () => {
    playBtn.textContent = '▶'
    cancelAnimationFrame(rafId)
    publish()
  })
  video.addEventListener('seeked', publish)
  video.addEventListener('timeupdate', () => {
    if (video.paused) publish()
  })

  function updateReadout() {
    readout.firstChild.textContent = formatTime(video.currentTime)
    readout.lastChild.textContent = ` / ${formatTime(state.video.duration)}`
    if (!scrubbing) seekInput.value = String(video.currentTime)
  }

  function togglePlay() {
    if (!state.video.url) {
      toast('Load a video first', { kind: 'warn' })
      return
    }
    if (video.paused || video.ended) video.play()
    else video.pause()
  }
  playBtn.addEventListener('click', togglePlay)

  function seekTo(time, { pause = false } = {}) {
    if (!state.video.url) {
      toast('Load a video first', { kind: 'warn' })
      return
    }
    if (pause) video.pause()
    const max = Number.isFinite(video.duration) ? video.duration : Infinity
    video.currentTime = Math.min(Math.max(time, 0), max)
  }

  function stepFrames(frames) {
    if (!state.video.url) return
    video.pause()
    seekTo(video.currentTime + frames / (state.video.fps || 30))
  }

  let currentSpeed = 1
  function cycleSpeed(direction) {
    const index = SPEEDS.indexOf(currentSpeed)
    const next = SPEEDS[Math.min(SPEEDS.length - 1, Math.max(0, index + direction))]
    currentSpeed = next
    video.playbackRate = next
    speedLabel.textContent = `${next}×`
  }

  // --- Scrubbing -----------------------------------------------------------
  seekInput.addEventListener('pointerdown', () => (scrubbing = true))
  seekInput.addEventListener('pointerup', () => (scrubbing = false))
  seekInput.addEventListener('input', () => {
    if (!state.video.url) return
    video.currentTime = Number.parseFloat(seekInput.value)
  })

  // --- Tagging -------------------------------------------------------------
  function tagSelected() {
    if (!state.video.url) {
      toast('Load a video first — the timestamp comes from the playhead', { kind: 'warn' })
      return
    }
    const index = state.selection
    const move = state.pgn.moves[index]
    if (!move) {
      toast('Load a PGN and select a move to tag', { kind: 'warn' })
      return
    }
    setStamp(index, video.currentTime)
    toast(`${figurineLabel(move)} → ${formatTime(video.currentTime)}`)
    selectMove(Math.min(index + 1, state.pgn.moves.length - 1), { reveal: true })
  }
  tagBtn.addEventListener('click', tagSelected)

  // --- Tick marks on the seek bar ------------------------------------------
  function renderTicks() {
    clear(ticks)
    const { duration } = state.video
    if (!duration) return
    state.stamps.forEach((stamp) => {
      if (stamp == null) return
      ticks.appendChild(
        h('span.seek-tick', { style: { left: `${(Math.min(stamp, duration) / duration) * 100}%` } }),
      )
    })
  }

  // --- Visibility / restore states -----------------------------------------
  function updateStageState() {
    const loaded = Boolean(state.video.url)
    video.hidden = !loaded
    drop.hidden = loaded
    if (!loaded && state.video.fileName) {
      dropTitle.textContent = `Re-attach “${state.video.fileName}”`
      browseBtn.textContent = 'Browse for the video'
    }
  }

  // --- Bus wiring -----------------------------------------------------------
  on('video', () => {
    updateStageState()
    seekInput.max = String(state.video.duration || 0)
    renderTicks()
    updateReadout()
    layoutVideo()
  })
  on('overlay', layoutVideo)
  on('stamps', renderTicks)
  on('selection', updateTagBtn)
  on('pgn', updateTagBtn)
  on('seek', ({ time }) => seekTo(time))
  updateTagBtn()

  // --- Keyboard --------------------------------------------------------------
  window.addEventListener('keydown', (e) => {
    if (isTyping(e) || document.querySelector('.dialog-overlay')) return
    const shift = e.shiftKey
    switch (e.key) {
      case ' ':
        e.preventDefault()
        togglePlay()
        break
      case ',':
        e.preventDefault()
        stepFrames(shift ? -10 : -1)
        break
      case '.':
        e.preventDefault()
        stepFrames(shift ? 10 : 1)
        break
      case 'ArrowLeft':
        e.preventDefault()
        seekTo(video.currentTime - (shift ? 5 : 1))
        break
      case 'ArrowRight':
        e.preventDefault()
        seekTo(video.currentTime + (shift ? 5 : 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        selectMove(Math.max(0, state.selection - 1), { reveal: true })
        break
      case 'ArrowDown':
        e.preventDefault()
        selectMove(Math.min(state.pgn.moves.length - 1, state.selection + 1), { reveal: true })
        break
      case 'Enter':
        e.preventDefault()
        tagSelected()
        break
      case 'Backspace': {
        e.preventDefault()
        if (state.selection >= 0 && state.stamps[state.selection] != null) {
          clearStamp(state.selection)
          toast(`Cleared ${figurineLabel(state.pgn.moves[state.selection])}`)
        }
        break
      }
      case '[':
        e.preventDefault()
        cycleSpeed(-1)
        break
      case ']':
        e.preventDefault()
        cycleSpeed(1)
        break
      case 'j':
      case 'J': {
        e.preventDefault()
        const stamp = state.stamps[state.selection]
        if (stamp != null) seekTo(stamp)
        break
      }
    }
  })

  updateStageState()
  return stageEl
}

function isTyping(e) {
  return Boolean(e.target.closest?.('input, textarea, select, [contenteditable]'))
}
